import * as runtime from '../runtime';
import type {
  DeleteProfilePictureResponse,
  ErrorResource,
  UploadProfilePictureResponse,
} from '../models';

export interface UploadProfilePictureRequest {
  profilePicture: Blob;
}

/**
 *
 */
export class AgentApi extends runtime.BaseAPI {
  async deleteProfilePictureRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<DeleteProfilePictureResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/agents/me/profile-picture`,
        method: 'DELETE',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Removes the profile picture for the currently authenticated agent/user. The blob is deleted from Vercel Blob storage and the database field is cleared.
   * Delete the authenticated agent\'s profile picture
   */
  async deleteProfilePicture(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<DeleteProfilePictureResponse> {
    const response = await this.deleteProfilePictureRaw(initOverrides);
    return await response.value();
  }

  async uploadProfilePictureRaw(
    requestParameters: UploadProfilePictureRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<UploadProfilePictureResponse>> {
    if (
      requestParameters.profilePicture === null ||
      requestParameters.profilePicture === undefined
    ) {
      throw new runtime.RequiredError(
        'profilePicture',
        'Required parameter requestParameters.profilePicture was null or undefined when calling uploadProfilePicture.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const consumes: runtime.Consume[] = [
      { contentType: 'multipart/form-data' },
    ];
    // @ts-ignore: canConsumeForm may be unused
    const canConsumeForm = runtime.canConsumeForm(consumes);

    let formParams: { append(param: string, value: any): any };
    let useForm = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    useForm = canConsumeForm;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }

    if (requestParameters.profilePicture !== undefined) {
      formParams.append(
        'profilePicture',
        requestParameters.profilePicture as any,
      );
    }

    const response = await this.request(
      {
        path: `/agents/me/profile-picture`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: formParams,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Uploads a profile picture for the currently authenticated agent/user. The image is stored in Vercel Blob storage. Any previously stored picture is replaced in the database (old blob is deleted automatically).
   * Upload or replace the authenticated agent\'s profile picture
   */
  async uploadProfilePicture(
    requestParameters: UploadProfilePictureRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<UploadProfilePictureResponse> {
    const response = await this.uploadProfilePictureRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
